import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Typography,
  AppBar,
  Toolbar,
  Hidden,
  IconButton,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import TooltipIconButton from '../../widgets/TooltipIconButton';
import { AppState } from '../../state/MyStateProvider';
import MyAvatar from '../../widgets/avatar/MyAvatar';
import MyLinearProgress from '../../widgets/MyLinearProgress';
import { seedDashboard3DrawersIcons } from '../../icon/seed-icons';
import { APP_STATE_ACTION_TYPE_USER } from '../../state/seed-states';
import D3DPopover from './D3DPopover';
import { dashboard3DrawersIconsPropTypes, d3dPopoverMenusPropTypes, defaultD3dPopoverMenusPropTypes } from './d3d-prop-types';
import { FirebaseState } from '../../firebase/MyFirebaseProvider';

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.background.appBar,
    transition: theme.transitions.create('background-color', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    // marginLeft: drawerWidth,
    // [theme.breakpoints.up("sm")]: {
    //   width: `calc(100% - ${drawerWidth}px)`,
    // },
  },
  logo: {
    height: 50,
    marginRight: theme.spacing(1),
  },
  onAppBar: {
    color: theme.palette.common.white,
  },
  grow: {
    flexGrow: 1,
  },
  actions: {
    // display: "none",
    // [theme.breakpoints.up("md")]: {
    display: 'flex',
    alignItems: 'center',
    // },
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  avatarButton: {
    marginLeft: theme.spacing(1),
    padding: 0,
  },
}));

const D3DMyAvatar = ({
  setUserMenuAnchor,
  avatar,
  profileTitle,
}) => {
  const classes = useStyles();

  return (
    <IconButton
      className={classes.avatarButton}
      onClick={(e) => {
        setUserMenuAnchor(e.currentTarget);
      }}
    >
      <MyAvatar
        src={avatar}
        alt={profileTitle}
        name={profileTitle}
      />
    </IconButton>
  );
};

D3DMyAvatar.propTypes = {
  setUserMenuAnchor: PropTypes.func.isRequired,
  avatar: PropTypes.string,
  profileTitle: PropTypes.string,
};

D3DMyAvatar.defaultProps = {
  avatar: undefined,
  profileTitle: undefined,
};

const D3DTopAppBar = (props) => {
  const {
    AuthenticationRepository,
    isLoading,
    onDrawerToggle,
    onDrawerToggleMobile,
    useBackButton,
    icons,
    backButtonAction,
    titleId,
    onClickBack,
    applicationName,
    logo,
    popoverMenus,
    popoverProfilePath,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { state, dispatch } = React.useContext(AppState);
  const { firebaseConfig } = React.useContext(FirebaseState);

  const authRepo = new AuthenticationRepository(state, dispatch, firebaseConfig);
  const user = state[APP_STATE_ACTION_TYPE_USER];
  let avatar;
  let profileTitle;
  let profileSubtitle;
  if (user) {
    avatar = user.avatar.avatar;
    profileTitle = user.avatar.title;
    profileSubtitle = user.avatar.subtitle;
  }

  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);

  const handleClickLogout = () => {
    authRepo.logout();
  };

  return (
    <AppBar
      position="fixed"
      className={classes.appBar}
    >
      <MyLinearProgress isLoading={isLoading} />
      <Toolbar>
        {/* Mobile-only */}
        <Hidden lgUp implementation="css">
          {
              useBackButton
                ? (
                  <>
                    <Hidden xsDown implementation="css">
                      <TooltipIconButton
                        edge="start"
                        onClick={onDrawerToggleMobile}
                        className={clsx([classes.menuButton, classes.onAppBar])}
                        icon={<icons.Menu />}
                        title={<FormattedMessage id="seed.button.menu.open" />}
                      />
                    </Hidden>
                    <Hidden smUp implementation="css">
                      <TooltipIconButton
                        edge="start"
                        onClick={onClickBack}
                        className={clsx([classes.menuButton, classes.onAppBar])}
                        icon={<icons.ArrowBack />}
                        title={<FormattedMessage id="seed.button.back" />}
                      />
                    </Hidden>
                  </>
                )
                : (
                  <TooltipIconButton
                    edge="start"
                    onClick={onDrawerToggleMobile}
                    className={clsx([classes.menuButton, classes.onAppBar])}
                    icon={<icons.Menu />}
                    title={<FormattedMessage id="seed.button.menu.open" />}
                  />
                )
            }
        </Hidden>
        {/* Non-mobile only */}
        <Hidden mdDown implementation="css">
          <TooltipIconButton
            edge="start"
            onClick={onDrawerToggle}
            className={clsx([classes.menuButton, classes.onAppBar])}
            icon={<icons.Menu />}
            title={<FormattedMessage id="seed.button.menu.open" />}
          />
        </Hidden>
        {
          isMobile
            ? (
              <Typography variant="h6" noWrap className={classes.onAppBar}>
                <FormattedMessage id={titleId} />
              </Typography>
            )
            : (
              <>
                <img className={classes.logo} src={logo} alt="Logo" />
                <Typography variant="h6" noWrap className={classes.onAppBar}>
                  { applicationName }
                </Typography>
              </>
            )
        }
        <div className={classes.grow} />
        <div className={classes.actions}>
          {/* <Hidden xsDown className={classes.actions}>
            <IntlSelect
              className={classes.onAppBar}
            />
            <ThemeIconButton
              className={classes.onAppBar}
            />
            <TooltipIconButton
              className={classes.onAppBar}
              title={<FormattedMessage id="dashboard3Drawers.menu.notifications" />}
              icon={<icons.Notifications />}
            />
          </Hidden> */}
          {
              useBackButton
                ? (
                  <>
                    <Hidden smUp implementation="css">
                      {backButtonAction}
                    </Hidden>
                    <Hidden xsDown implementation="css">
                      <D3DMyAvatar
                        setUserMenuAnchor={setUserMenuAnchor}
                        avatar={avatar}
                        profileTitle={profileTitle}
                      />
                    </Hidden>
                  </>
                )
                : (
                  <D3DMyAvatar
                    setUserMenuAnchor={setUserMenuAnchor}
                    avatar={avatar}
                    profileTitle={profileTitle}
                  />
                )
            }

          <D3DPopover
            anchorEl={userMenuAnchor}
            onClose={() => setUserMenuAnchor(null)}
            onClickLogout={handleClickLogout}
            avatar={avatar}
            profileTitle={profileTitle}
            profileSubtitle={profileSubtitle}
            menus={popoverMenus}
            profilePath={popoverProfilePath}
          />
        </div>
      </Toolbar>
    </AppBar>
  );
};

D3DTopAppBar.propTypes = {
  AuthenticationRepository: PropTypes.func.isRequired,
  titleId: PropTypes.string.isRequired,
  onDrawerToggle: PropTypes.func.isRequired,
  onDrawerToggleMobile: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  useBackButton: PropTypes.bool,
  icons: dashboard3DrawersIconsPropTypes,
  backButtonAction: PropTypes.node,
  onClickBack: PropTypes.func,
  applicationName: PropTypes.string.isRequired,
  popoverMenus: d3dPopoverMenusPropTypes,
  popoverProfilePath: PropTypes.string,
};

D3DTopAppBar.defaultProps = {
  isLoading: false,
  useBackButton: false,
  icons: seedDashboard3DrawersIcons,
  backButtonAction: undefined,
  onClickBack: undefined,
  popoverMenus: defaultD3dPopoverMenusPropTypes,
  popoverProfilePath: undefined,
};

export default D3DTopAppBar;
